/**
 * Trava de MISTURA: uma tradução de exibição ou vem inteira, ou não vem.
 *
 * Caso real (QA do Robson, chat #1434, 28/10/2026): a confirmação da corretiva
 * apareceu como "Equipment: QA53-CHL-001. Qual é a urgência?" numa tela em
 * português. O provedor devolveu a pergunta traduzida e deixou o rótulo em
 * inglês; as travas só conferiam os marcadores de proteção (`restore`), então
 * a metade-e-metade foi para a tela. Aqui: se uma parte de prosa voltou
 * IDÊNTICA e é de outro idioma que não o da tela, enquanto outra parte da mesma
 * resposta foi traduzida, a tradução é recusada e o texto fica inteiro no
 * idioma de origem (nunca meio a meio).
 */
const SCREEN_LANGUAGE: Record<string, string> = { en: 'eng', pt: 'por', es: 'spa' }
const MIN_PROSE_LETTERS = 6
const MIN_MARGIN = 0.2
const normalize = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase()
// Codes, numbers and URLs carry no language; only words without digits count.
const proseOf = (part: string) =>
  (part.replace(/https?:\/\/\S+/g, ' ').match(/\p{L}+(?:['’-]\p{L}+)*(?![\p{L}\p{N}-])/gu) ?? [])
    .filter((word) => !/\d/.test(word))
    .join(' ')
// Text the presenter never translates (code, link targets, names) may come back untouched.
const NEVER_TRANSLATED = /```[\s\S]*?```|`[^`\n]+`|\]\([^)]*\)|\[(?:doc|wo|norma|equip):[^\]]+\]/g
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const lettersOf = (text: string) => text.match(/\p{L}/gu)?.length ?? 0

/** `true` quando a tradução deixou, sem traduzir, prosa de outro idioma ao lado de prosa traduzida. */
export async function leavesForeignProseUntranslated(
  source: string,
  translated: string,
  locale: string,
  protectedValues: readonly string[] = [],
): Promise<boolean> {
  const target = SCREEN_LANGUAGE[locale.toLowerCase().split('-')[0]]
  if (!target) return false
  // Valores protegidos vêm do motor e podem trazer vazio, nulo ou ausente.
  const names = protectedValues
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 2)
    .sort((a, b) => b.length - a.length)
  // Só entra no casador o que de fato aparece neste texto: dezenas de códigos de
  // material compartilhados não podem inflar a expressão de cada tarefa.
  const hide = (text: string) => {
    const present = names.filter((name) => text.includes(name))
    const withoutNames = present.length
      ? text.replace(new RegExp(present.map(escape).join('|'), 'g'), ' ')
      : text
    return /[`[]|\]\(/.test(withoutNames)
      ? withoutNames.replace(NEVER_TRANSLATED, ' ')
      : withoutNames
  }
  const visible = hide(source)
  const parts = visible
    .split(/\n+|(?<=[.!?])\s+/)
    .map((part) => ({ part: normalize(part), prose: proseOf(part) }))
    .filter(({ prose }) => lettersOf(prose) >= MIN_PROSE_LETTERS)
  const result = normalize(hide(translated))
  const unchanged = parts.filter(({ part }) => result.includes(part))
  if (!unchanged.length || unchanged.length === parts.length) return false
  try {
    const { francAll } = await import('franc-min')
    // Palavra solta parecida entre idiomas ("Equipamento" x "Equipment") perde a
    // margem; se ela é do mesmo idioma que a resposta inteira tinha na origem,
    // continua sendo prosa que ficou sem traduzir.
    const whole = parts.map(({ prose }) => prose).join(' ')
    const sourceLanguage = francAll(whole, { only: ['eng', 'por', 'spa'], minLength: 3 })[0]?.[0]
    return unchanged.some(({ prose }) => {
      const scores = francAll(prose, { only: ['eng', 'por', 'spa'], minLength: 3 })
      const top = scores[0]?.[0]
      const own = scores.find(([language]) => language === target)?.[1] ?? 0
      if (top === target) return false
      return 1 - own >= MIN_MARGIN || (top === sourceLanguage && sourceLanguage !== target)
    })
  } catch {
    return false // detector indisponível: não bloqueia, a trava do provedor segue valendo
  }
}
